import React from 'react'
import { isMobile } from 'react-device-detect'
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons'
import { MapIcon, PencilIcon } from 'lucide-react'
import {
  $DCDangerColor,
  $DCDangerLightColor,
  $DCInfoColor,
  $DCInfoLightColor,
  $DCSuccessColor,
  $DCSuccessLightColor,
  $DCWarningColor,
  $DCWarningLightColor,
  DcButton,
  DcOverlaySlider,
  DcSwitch,
  DcTag,
  Link,
  PixelDiv,
  PixelFactoryContext,
  PixelFlexBox,
  PixelIcon,
  PixelPageContainer,
  PixelSpinner,
  PixelTable,
  PixelText,
  Section,
  SectionActions,
  SectionContent,
  SectionHeader,
  SectionTitle,
} from 'pixel-base'
import { AjaxResponseType } from 'rxjs/internal/ajax/types'
import styled from 'styled-components'

import { get, put, unmapPickupAddress } from 'utils/api-routes.util'

import MapCourier from './AddCourierMapping'
import WarehouseForm from './WarehouseForm'
import { IPixelFactory } from '../../../../../../decl.d'

export interface IWarehouseDetail {
  id?: string
  name: string
  warehouse_code: string
  is_default?: string
  address: string
  description: string
  city: string
  zip?: null
  province?: null
  country?: null
  phone: string
  active?: string
  created_at?: Date
  created_by_user_id?: string
  created_by?: string
  zones: Zone[]
  couriers?: Courier[]
}

export interface Courier {
  courier_id: null
  source_pickup_id: string
  courier_pickup_address: null | string
  status: null | string
  default: string
  courier_name: string
  courier_company: string
  image_path: string
}

export interface Zone {
  id: string
  zone_name: string
}

const WarehouseDetail = (props: { warehouseID: string }) => {
  const [showModal, setShowModal] = React.useState(false)
  const [isShowSpinner, setIsShowSpinner] = React.useState(false)
  const { warehouseID } = props

  const courierDetailsRef = React.useRef(null)
  const [showCourierModel, setShowCourierModel] = React.useState(false)
  const {
    showPageLoader,
    hidePageLoader,
    showSuccess,
    showFailure,
    pixelConfirm,
  } = React.useContext<IPixelFactory>(PixelFactoryContext)
  const [warehouse, setWarehouse] = React.useState<IWarehouseDetail>({
    id: '',
    name: '',
    warehouse_code: '',
    is_default: '',
    address: '',
    description: '',
    city: '',
    zip: null,
    province: null,
    country: null,
    phone: '',
    active: '',
    created_at: new Date(),
    created_by_user_id: '',
    created_by: '',
    zones: [],
    couriers: [],
  })
  const readData = () => {
    setIsShowSpinner(true)
    if (warehouseID === null) return
    get(`/warehouse/${warehouseID}`).subscribe((res: any) => {
      const warehouse = res.response
      setWarehouse(warehouse)

      setIsShowSpinner(false)
    })
  }

  const [zonesList, setZonesList] = React.useState<any>([])
  const getZones = () => {
    get('/location/zones').subscribe((res: any) => {
      const zones = res.response.data
      if (res.response.data) {
        setZonesList(zones)
      }
    })
  }

  React.useEffect(() => {
    getZones()
    readData()
  }, [warehouseID])

  const updateStatus = (e) => {
    const postData = {
      active: e.target.checked,
    }
    setIsShowSpinner(true)
    put(`/warehouse/active/${warehouse.id}`, postData).subscribe((res) => {
      setIsShowSpinner(false)
      if (res.response.data) {
        setWarehouse(res.response?.data)
        showSuccess('Successfully updated')
      } else {
        showFailure('Error in updating the status')
      }
    })
  }

  const handleUnMapPickupAddress = (row) => {
    const data = {
      courier_id: row?.courier_id,
      warehouse_id: warehouse?.id,
      source_pickup_id: row?.source_pickup_id,
    }
    pixelConfirm({
      title: 'Unmap Pickup Address',
      message: 'Are you sure? you want to unmap Pickup Address.',
      onConfirm: () => {
        showPageLoader()
        unmapPickupAddress(data).subscribe((res: AjaxResponseType<any>) => {
          hidePageLoader()
          if (res?.response?.data && res?.response?.data?.id) {
            showSuccess(
              res?.response?.message ?? 'Pickup Address un mapped successfully',
            )
            const updatedCouriers = warehouse?.couriers?.filter(
              (courier) => courier.source_pickup_id !== row.source_pickup_id,
            )

            setWarehouse((prevState) => ({
              ...prevState,
              couriers: updatedCouriers,
            }))
          } else {
            showFailure(`Something went wrong, ${res.response.error}`)
          }
        })
      },
    })
  }

  const columns = [
    {
      dataKey: 'image_path',
      name: <PixelText>Courier Logo</PixelText>,
      sortable: true,
      width: '9%',
      selector: (row: Courier) => <CourierImage src={row.image_path} />,
    },
    {
      dataKey: 'name',
      name: <PixelText>Courier Name</PixelText>,
      width: '13%',
      sortable: true,
      selector: (row: Courier) => row.courier_name || '--',
    },
    {
      dataKey: 'source_pickup_id',
      name: <PixelText>Source Pickup ID</PixelText>,
      sortable: true,
      width: '15%',
      selector: (row: Courier) => row.source_pickup_id || '--',
    },
    {
      dataKey: 'courier_pickup_address',
      name: <PixelText>Courier Pickup Address</PixelText>,
      sortable: true,
      width: '30%',
      selector: (row: Courier) => (
        <PixelText multiLine>{row.courier_pickup_address || '--'}</PixelText>
      ),
    },
    {
      dataKey: 'status',
      name: <PixelText>Status</PixelText>,
      sortable: true,
      width: '9%',
      selector: (row: Courier) => (
        <DcTag
          customBackgroundColor={
            row.status === 'active' || row.status == 'Active'
              ? $DCSuccessLightColor
              : row.status === 'failed'
              ? $DCDangerLightColor
              : row.status === 'queued'
              ? $DCWarningLightColor
              : $DCInfoLightColor
          }
          textColor={
            row.status === 'active' || row.status == 'Active'
              ? $DCSuccessColor
              : row.status === 'failed'
              ? $DCDangerColor
              : row.status === 'queued'
              ? $DCWarningColor
              : $DCInfoColor
          }
        >
          {row.status}
        </DcTag>
      ),
    },
    {
      dataField: 'mappedwarehouse',
      name: <PixelText>Map Courier</PixelText>,
      sortable: true,
      width: '12%',
      selector: (row: Courier) => (
        <PixelFlexBox gap="10px">
          {row.courier_name ? (
            <Link
              onClick={() => {
                window.open(`/admin/couriers/details/${row.courier_id}`)
              }}
            >
              {row.courier_name}
            </Link>
          ) : (
            <PixelDiv padding="0px 0px 0px 30px">
              <PixelText>Courier Not Mapped</PixelText>
            </PixelDiv>
          )}
        </PixelFlexBox>
      ),
    },
    {
      name: <PixelText>Action</PixelText>,
      sortable: true,
      width: '9%',
      selector: (row) => (
        <PixelIcon
          onClick={() => {
            handleUnMapPickupAddress(row)
          }}
          showTooltip
          color={'red'}
          tooltip="Unmap Warehouse Pickup Address"
          icon={faXmark}
        />
      ),
    },
  ]
  return (
    <>
      <PixelSpinner isShow={isShowSpinner} />

      <PixelPageContainer style={{ padding: '0px' }} isMobile={isMobile}>
        <Section ref={courierDetailsRef} isMobile={isMobile}>
          <SectionHeader>
            <SectionTitle>{warehouse.name}</SectionTitle>
            <SectionActions>
              <DcSwitch
                className={'large'}
                onChange={updateStatus}
                label={warehouse.active != '0' ? 'Active' : 'Inactive'}
                checked={warehouse.active != '0' ? true : false}
              />
              <DcButton
                size="sm"
                onClick={() => {
                  setShowModal(true)
                }}
                variant="outline-primary"
                withIcon={true}
              >
                <PencilIcon size={16} />
                <span>Edit</span>
              </DcButton>
            </SectionActions>
          </SectionHeader>
          <SectionContent isMobile={isMobile}>
            <InfoItem>
              <Label>Code:</Label>
              <Detail>{warehouse.warehouse_code}</Detail>
            </InfoItem>
            <InfoItem>
              <Label>City:</Label>
              <Detail>{warehouse.city || '--'}</Detail>
            </InfoItem>
            <InfoItem>
              <Label>Address:</Label>
              <Detail>{warehouse.address || '--'}</Detail>
            </InfoItem>
            <InfoItem>
              <Label>Is Default :</Label>
              <Detail>
                {warehouse.is_default === '1' ? (
                  <PixelIcon color={'green'} icon={faCheck} />
                ) : (
                  <PixelIcon color={'red'} icon={faXmark} />
                )}
              </Detail>
            </InfoItem>
            <InfoItem>
              <Label>Phone:</Label>
              <Detail>{warehouse.phone || '--'}</Detail>
            </InfoItem>
            <InfoItem>
              <Label>Zones:</Label>
              <Detail>
                {warehouse?.zones?.map((zone: any, index) => (
                  <div key={index}>{zone.zone_name}</div>
                ))}
              </Detail>
            </InfoItem>
            <InfoItem>
              <Label>Description:</Label>
              <Detail>{warehouse.description || '--'}</Detail>
            </InfoItem>
          </SectionContent>
        </Section>
        <Section>
          <SectionHeader>
            <SectionTitle>
              This warehouse serves following courier pick up locations
            </SectionTitle>
            <SectionActions>
              <PixelFlexBox padding={'0 0 10px 0'} justifyContent="flex-end">
                <DcButton
                  onClick={() => {
                    setShowCourierModel(true)
                  }}
                  size="sm"
                  variant="outline-primary"
                  withIcon={true}
                >
                  <MapIcon size={16} />
                  <span>Map Courier</span>
                </DcButton>
              </PixelFlexBox>
            </SectionActions>
          </SectionHeader>
          <SectionContent>
            {warehouse.couriers?.length === 0 ? (
              'No couriers found'
            ) : (
              <PixelTable
                columns={columns}
                data={warehouse.couriers || []}
                pagination={warehouse.couriers?.length > 10}
              />
            )}
          </SectionContent>
        </Section>
      </PixelPageContainer>
      {showModal && (
        <DcOverlaySlider
          width="50%"
          title={'Edit Warehouse'}
          onClose={() => setShowModal(false)}
          isOpen={showModal}
        >
          <SectionContent>
            <WarehouseForm
              show={showModal}
              warehouse={warehouse}
              zonesList={zonesList}
              warehouseId={warehouseID}
              handleClose={(data, action) => {
                if (action === 'update') {
                  setWarehouse({
                    ...warehouse,
                    ...data,
                  })
                }
                setShowModal(false)
              }}
            />
          </SectionContent>
        </DcOverlaySlider>
      )}

      <DcOverlaySlider
        width="50%"
        title={'Map Courier'}
        onClose={() => setShowCourierModel(false)}
        isOpen={showCourierModel}
      >
        <SectionContent>
          <MapCourier
            warehouse={warehouse}
            handleClose={(data: Courier) => {
              if (data) {
                readData()
              }
              setShowCourierModel(false)
            }}
          />
        </SectionContent>
      </DcOverlaySlider>
    </>
  )
}

const InfoItem = styled.div`
  display: table;
  table-layout: fixed;
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;
  &:last-child {
    border-bottom: none;
  }
`
const Label = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #8e959b;
  display: table-cell;
  vertical-align: middle;
  padding-right: 10px;
  /* padding: 10px; */
  /* background-color: #e9ecef; */
  width: 40%;
`
const Detail = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #8e959b;
  display: table-cell;
  vertical-align: middle;
  padding-left: 10px;
  width: 60%;
  /* background-color: #e9ecef78; */
`

const CourierImage = styled.img`
  width: 30px;
  max-height: 30px;
  border: 1px solid #d4d4d4;
`
export default WarehouseDetail
